import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    project_type: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        project_type: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header />
      
      <div className="page-header" data-testid="contact-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="section-subtitle">Get in Touch</p>
            <h1 className="section-title">Contact Us</h1>
            <p className="section-description">
              Have a project in mind? Looking to book talent? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </div>

      <section className="section section-dark" data-testid="contact-section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4rem', maxWidth: '1000px', margin: '0 auto' }}>
            
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
                <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '50%', 
                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    color: 'var(--primary)'
                  }}>
                    <Mail size={24} />
                  </div>
                  <h4 style={{ marginBottom: '0.5rem' }}>Email</h4>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>info@woodhouseandarcher.com</p>
                </div>

                <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '50%', 
                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    color: 'var(--primary)'
                  }}>
                    <Phone size={24} />
                  </div>
                  <h4 style={{ marginBottom: '0.5rem' }}>Phone</h4>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Available upon request</p>
                </div>

                <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '50%', 
                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    color: 'var(--primary)'
                  }}>
                    <MapPin size={24} />
                  </div>
                  <h4 style={{ marginBottom: '0.5rem' }}>Locations</h4>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>London, UK</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Cairo, Egypt</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Los Angeles, CA</p>
                </div>
              </div>

              {/* Instagram CTA */}
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <a 
                  href="https://instagram.com/woodhouseandarcher" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  data-testid="contact-instagram-link"
                >
                  <Instagram size={18} />
                  Follow @woodhouseandarcher
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              className="contact-form-container"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 style={{ marginBottom: '1.5rem', fontFamily: 'Playfair Display, serif' }}>Send us a message</h3>
              
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Project Type</label>
                    <select
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleChange}
                      className="form-select"
                      data-testid="contact-project-type"
                    >
                      <option value="">Select...</option>
                      <option value="film">Film</option>
                      <option value="television">Television</option>
                      <option value="commercial">Commercial</option>
                      <option value="theater">Theater</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="form-input"
                    required
                    data-testid="contact-subject-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  style={{ width: '100%' }}
                  disabled={submitting}
                  data-testid="contact-submit-button"
                >
                  {submitting ? 'Sending...' : (
                    <>
                      Send Message
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
